import React, { useRef } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box } from "@mui/material";
import useStyles from "./calendar.style";
// @ts-ignore - moment locale side-effect import
import "moment/locale/hu";
import {
  MyTimeCalendarEventWrapper,
  MyTimeCalendarMonthEvent,
  MyTimeCalendarMonthlyDateHeader,
  MyTimeCalendarToolbar,
  MyTimeCalendarWeeklyDayHeader,
  MyTimeCalendarWeeklyEvent,
} from "my-time/components";
import { OcCalendarDayHeader } from "oc/components";
import { useOcContractCalendar } from "oc/hooks";

type MyTimeCalendarProps = {
  contract: any;
  events: any[];
  handleSelectRow: (params: { action: string; row: any }) => void;
  date: Date | string;
  onDateChange: (date: Date) => void;
  handleMoreClick: (date: Date) => void;
  onClickDisabled?: boolean;
  isNotExaminer?: boolean;
  countTimeData?: any;
};

export const MyTimeCalendar: React.FC<MyTimeCalendarProps> = ({
  contract,
  events,
  handleSelectRow,
  date,
  onDateChange,
  handleMoreClick,
  onClickDisabled = false,
  isNotExaminer,
  countTimeData,
}) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();

  let calendarRef = useRef<any>(null);

  const localizer = momentLocalizer(moment);
  (localizer as any).segmentOffset = 0;

  const culture = i18n.language === "hu" ? "hu" : "en";
  let messages = {
    date: t("OC_CALENDAR_DATE"),
    time: t("OC_CALENDAR_TIME"),
    event: t("OC_CALENDAR_EVENT"),
    allDay: t("OC_CALENDAR_ALL_DAY"),
    week: t("OC_CALENDAR_WEEK"),
    work_week: t("OC_CALENDAR_WORK_WEEK"),
    day: t("OC_CALENDAR_DAY"),
    month: t("OC_CALENDAR_MONTH"),
    previous: t("OC_CALENDAR_PREVIOUS"),
    next: t("OC_CALENDAR_NEXT"),
    yesterday: t("OC_CALENDAR_YESTERDAY"),
    tomorrow: t("OC_CALENDAR_TOMORROW"),
    today: t("OC_TODAY"),
    agenda: t("OC_CALENDAR_AGENDA"),
    noEventsInRange: t("OC_CALENDAR_NO_EVENTS_IN_RANGE"),
    showMore: (total: number) => `+${total} ${t("OC_CALENDAR_MORE")}`,
  };

  const firstDate = moment(date).startOf("year").toDate();
  const lastDate = moment(date).endOf("year").toDate();

  const { contractCalendar } = useOcContractCalendar({
    contract: contract,
    startDate: firstDate.toISOString(),
    endDate: lastDate.toISOString(),
  });

  const customBackground = (date: Date) => {
    if (date && contractCalendar) {
      const day = moment(date).dayOfYear();
      const type = contractCalendar[day - 1];
      if (type === "P") {
        return "rbc-weekend-day";
      }
      if (type === "F") {
        return "rbc-payed-holiday-day";
      }
    }
    return "";
  };

  const eventPropGetter = (event: any) => {
    let actualClass = "";
    switch (event.myTimeType) {
      case "OVERTIME":
        actualClass = "rbc-event-my-time-overtime";
        break;
      case "WORKING_TIME":
        actualClass = "rbc-event-my-time-working-time";
        break;
      case "WORKING_TIME_ITEM":
        actualClass = "rbc-event-my-time-working-time-item";
        break;
      case "HOMEOFFICE":
        actualClass = "rbc-event-my-time-homeoffice";
        break;
      case "ON_CALL":
        actualClass = "rbc-event-my-time-on-call";
        break;
      case "STAND_BY":
        actualClass = "rbc-event-my-time-stand-by";
        break;
      case "STAND_BY_CALL":
        actualClass = "rbc-event-my-time-stand-by-call";
        break;
      case "PERSONAL_LEAVE":
        actualClass = "rbc-event-my-time-personal-leave";
        break;
      case "LEAVE":
        actualClass = "rbc-event-my-time-leave";
        break;
      case "ABSENCE":
        actualClass = "rbc-event-my-time-leave";
        break;
      case "OTHER":
        actualClass = "rbc-event-my-time-working-time-item";
        break;
      default:
        actualClass = "";
    }
    if (event.state && event.state === "requested") {
      actualClass = "rbc-event-my-time-requested";
    }
    if (event.state && event.state === "approved") {
      actualClass = "rbc-event-my-time-approved";
    }

    return { className: actualClass };
  };

  const onSelectEvent = (event: any) => {
    if (
      event.myTimeType === "WORKING_TIME_ITEM" ||
      event.myTimeType === "LEAVE" ||
      event.myTimeType === "ABSENCE" ||
      event.myTimeType === "OTHER" ||
      event.myTimeType === "HOMEOFFICE" ||
      event.myTimeType === "ON_CALL"
    ) {
      handleSelectRow({ action: "show", row: event });
    }
    if (event._id) {
      handleSelectRow({ action: "show", row: event });
      return;
    }
    if (isNotExaminer) {
      handleSelectRow({ action: "show", row: event });
      return;
    }
    if (event.myTimeType === "STAND_BY") {
      event.sender = "calendar";
      handleSelectRow({
        action: "show",
        row: event,
      });
      return;
    }
    handleSelectRow({ action: "edit", row: event });
  };

  const onSelectSlot = (slotInfo: any) => {
    if (onClickDisabled) return;
    const event = {
      date: slotInfo.start,
      myTimeType: "OVERTIME",
      startTime: "",
      endTime: "",
      timeDataTypeCode: "",
      timeDataTypeName: "",
      sender: "calendar",
    };
    handleSelectRow({ action: "add", row: event });
  };

  const onShowMore = (_events: any, dateVal: any) => {
    handleMoreClick(dateVal);
  };

  return (
    <Box
      style={{ height: "auto" }}
      className={classes.root}
    >
      <Calendar
        ref={calendarRef}
        date={new Date(date)}
        onNavigate={(d) => {
          onDateChange(d);
        }}
        onSelectEvent={(props) => onSelectEvent(props)}
        localizer={localizer}
        culture={culture}
        messages={messages}
        events={events}
        startAccessor="start"
        endAccessor="end"
        slotPropGetter={(d) => {
          return { className: customBackground(d as Date) };
        }}
        dayPropGetter={(d) => {
          return { className: customBackground(d as Date) };
        }}
        toolbar={true}
        tooltipAccessor={null}
        views={{ month: true, week: true }}
        // components-ben a nem szabványos kulcsok miatt cast
        components={
          {
            toolbar: MyTimeCalendarToolbar as any,
            month: {
              header: OcCalendarDayHeader,
              dateHeader: (props: any) => {
                return MyTimeCalendarMonthlyDateHeader({
                  ...props,
                  countTimeData: countTimeData,
                });
              },
              event: MyTimeCalendarMonthEvent as any,
              eventWrapper: MyTimeCalendarEventWrapper as any,
            },
            week: {
              header: (props: any) => {
                return MyTimeCalendarWeeklyDayHeader({
                  ...props,
                  isOffRange: (props as any).isOffRange,
                  countTimeData: countTimeData,
                });
              },
              event: MyTimeCalendarWeeklyEvent,
            },
          } as any
        }
        eventPropGetter={eventPropGetter}
        showMultiDayTimes={true}
        selectable={true}
        onSelectSlot={(slotInfo) => onSelectSlot(slotInfo)}
        onShowMore={(events, d) => onShowMore(events, d)}
        drilldownView="agenda"
      />
    </Box>
  );
};

export default MyTimeCalendar;
